/**
 * Utility functions for Policy components
 * Consolidates common logic to eliminate duplication
 */

import {
  DashboardPolicy,
  TenantPolicy,
  TenantBaseRule,
  TenantTargetRule,
  PolicyType,
  PolicyTabType,
  PolicyRule,
  RulePriority,
  RuleStatus,
  extractSitNames,
  extractLabelNames,
} from './types';

// GUID pattern (e.g. "97eab0c1-2894-4c12-91ad-a081654a90e3")
const GUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Returns true when the string looks like a GUID / UUID */
export function isGuid(value: string): boolean {
  return GUID_RE.test(value);
}

/**
 * Ensures a tenant name has the `.onmicrosoft.com` domain suffix.
 * - Already a full domain (contains a dot) → returned as-is.
 * - A GUID → returned as-is (cannot derive a domain from a GUID).
 * - A short name (e.g. "M365DS117641") → appends `.onmicrosoft.com`.
 */
export function ensureDomainSuffix(name: string): string {
  if (!name) return name;
  if (name.includes('.')) return name;   // already has a domain suffix
  if (GUID_RE.test(name)) return name;   // can't derive domain from GUID
  return `${name}.onmicrosoft.com`;
}

/**
 * Bidirectional mapping between PolicyType and PolicyTabType
 * Single source of truth - add new types here only
 */
export const POLICY_TYPE_TAB_MAP: Record<PolicyType, PolicyTabType> = {
  DlpCompliancePolicy: 'DLP',
  AutoSensitivityLabelPolicy: 'AutoLabel',
};

// Derive reverse map automatically
const TAB_POLICY_TYPE_MAP = Object.entries(POLICY_TYPE_TAB_MAP).reduce(
  (acc, [policyType, tab]) => {
    acc[tab as PolicyTabType] = policyType as PolicyType;
    return acc;
  },
  {} as Record<PolicyTabType, PolicyType>
);

export const mapPolicyTypeToTab = (policyType: PolicyType): PolicyTabType =>
  POLICY_TYPE_TAB_MAP[policyType];

export const mapTabToPolicyType = (tab: PolicyTabType): PolicyType =>
  TAB_POLICY_TYPE_MAP[tab];

/**
 * Filters policies by tab type
 * @param policies - Array of policies (Dashboard or Tenant)
 * @param selectedTab - Currently selected tab
 * @returns Filtered policies matching the selected tab
 */
export const filterPoliciesByTab = <T extends DashboardPolicy | TenantPolicy>(
  policies: T[],
  selectedTab: PolicyTabType
): T[] => {
  return policies.filter(policy => 
    mapPolicyTypeToTab(policy.policyType) === selectedTab
  );
};

/**
 * Counts policies by type for tab labels
 * @param policies - Array of all policies
 * @returns Object with DLP and AutoLabel counts
 */
export const countPoliciesByType = <T extends DashboardPolicy | TenantPolicy>(
  policies: T[]
): { dlpCount: number; autoLabelCount: number } => {
  let dlpCount = 0;
  let autoLabelCount = 0;
  
  policies.forEach(policy => {
    if (mapPolicyTypeToTab(policy.policyType) === 'DLP') {
      dlpCount++;
    } else {
      autoLabelCount++;
    }
  });
  
  return { dlpCount, autoLabelCount };
};

/**
 * Filters policies by search term and deployment status
 * Reusable for both Dashboard and Tenant views.
 * NOTE: status filter is also sent server-side; this handles any in-flight
 * UI status overrides (InProgress/DeploymentFailed) not yet reflected by the server.
 */
export const filterPolicies = <T extends DashboardPolicy | TenantPolicy>(
  policies: T[],
  searchTerm: string,
  statusFilter: string   // DeploymentStatus | 'All'
): T[] => {
  const query = searchTerm.toLowerCase().trim();

  return policies.filter(policy => {
    // Search filter - check policy name
    const matchesSearch = !query || policy.policyName.toLowerCase().includes(query);

    // Status filter — TenantPolicy has `status`; DashboardPolicy does not (per-tenant only).
    // Client-side filter is best-effort; server-side filter is the authoritative source.
    const matchesStatus =
      statusFilter === 'All' ||
      ('status' in policy && (policy as { status: string }).status === statusFilter);

    return matchesSearch && matchesStatus;
  });
};

/**
 * Converts TenantRule (Base or Target) to PolicyRule format
 * Used for rendering in RuleRow component
 */
export const convertTenantRuleToPolicyRule = (
  rule: TenantBaseRule | TenantTargetRule
): PolicyRule => {
  return {
    id: rule.ruleId,
    name: rule.ruleName,
    description: rule.description || '',
    priority: String(rule.priority) as RulePriority,
    status: rule.status as RuleStatus,
    sitNames: extractSitNames(rule.sits || []),
    labelNames: extractLabelNames(rule.labels || []),
  };
};

/**
 * Validates policy data for security issues
 * Sanitizes user input and checks for XSS
 */
export const sanitizePolicyName = (name: string): string => {
  // Remove potentially dangerous characters
  return name
    .replace(/[<>'"]/g, '') // Remove HTML special chars
    .trim()
    .slice(0, 255); // Enforce max length
};

/**
 * Validates search input
 */
export const sanitizeSearchInput = (input: string): string => {
  return input
    .replace(/[<>'"]/g, '')
    .trim()
    .slice(0, 100); // Reasonable search limit
};

/**
 * Creates stable sort key for policies
 * Ensures consistent ordering
 */
export const getPolicySortKey = (policy: DashboardPolicy | TenantPolicy): string => {
  return `${policy.priority}-${policy.policyName}`;
};

// ============================================================================
// SHARED TYPE DEFINITIONS
// ============================================================================

/**
 * A SIT or Label in a deployment summary.
 *
 * `key` is the internal match key (entityMatchKey) used to decide existence; `display` is the
 * text shown to the user. They are NOT interchangeable: two entities with different internal
 * names can share display text, so React lists must key on `key`, not `display`.
 */
export interface SummaryEntity {
  key: string;
  display: string;
}

/**
 * Base interface for deployment summaries
 * Shared fields between DeploySummary and TenantDeploySummary
 */
export interface BaseSummaryFields {
  totalRulesCount: number;
  sitsExisting: SummaryEntity[];
  sitsNew: SummaryEntity[];
  labelsExisting: SummaryEntity[];
  labelsNew: SummaryEntity[];
}

/**
 * Checks if all items in an array are selected
 */
export const areAllSelected = <T = string>(
  totalItems: number,
  selectedItems: Set<T>
): boolean => {
  return totalItems > 0 && selectedItems.size === totalItems;
};

/**
 * Checks if some (but not all) items are selected
 */
export const areSomeSelected = <T = string>(
  totalItems: number,
  selectedItems: Set<T>
): boolean => {
  return selectedItems.size > 0 && selectedItems.size < totalItems;
};
